import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import {DataSetMetricsState} from './dataSetMetrics';
import {
  createDataSetMetricSuccess,
  deleteDataSetMetricSuccess,
  loadDataSetMetrics,
  loadDataSetMetricsSuccess,
  loadPublicDataSetMetrics,
  loadPublicDataSetMetricsSuccess,
  updateDataSetMetrics,
  updateDataSetMetricsSuccess,
  updateDataSetMetricSuccess
} from './dataSetMetrics.actions';
import {TypedAction} from '@ngrx/store/src/models';
import {Serializer, Serializers} from '../../interfaces/serializer';
import {DataSetMetric} from '../../interfaces/metrics';
import {MetricComponentObject, MetricComponentObjects} from '../../classes/metric-component-object';
import {Sort} from '../../libraries/sort';

const _reducer: ActionReducer<DataSetMetricsState> = createReducer(
  {
    serialized: {
      all: [],
      source: [],
      normalized: [],
      calculated: []
    },
    componentObjects: {
      all: [],
      source: [],
      normalized: [],
      calculated: []
    },
    loading: false,
    loaded: false
  },
  on(loadDataSetMetrics, (state: DataSetMetricsState): DataSetMetricsState => {
    return {
      ...state,
      loading: true,
      loaded: false
    };
  }),
  on(loadDataSetMetricsSuccess, (state: DataSetMetricsState, action: TypedAction<string> & { metrics: Serializers<DataSetMetric> }): DataSetMetricsState => {
    const serializedSource: Serializers<DataSetMetric> = action.metrics.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'source');
    const serializedNormalized: Serializers<DataSetMetric> = action.metrics.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'normalized');
    const serializedCalculated: Serializers<DataSetMetric> = action.metrics.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'calculated');
    const componentObjectsAll: MetricComponentObjects = action.metrics.map((metric: Serializer<DataSetMetric>): MetricComponentObject => new MetricComponentObject(metric));

    return {
      ...state,
      serialized: {
        all: action.metrics,
        source: serializedSource,
        normalized: serializedNormalized,
        calculated: serializedCalculated
      },
      componentObjects: {
        all: _sort(componentObjectsAll),
        source: _sort(componentObjectsAll.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'source')),
        normalized: _sort(componentObjectsAll.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'normalized')),
        calculated: _sort(componentObjectsAll.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'calculated'))
      },
      loading: false,
      loaded: true
    };
  }),
  on(loadPublicDataSetMetrics, (state: DataSetMetricsState): DataSetMetricsState => {
    return {
      ...state,
      loading: true,
      loaded: false
    };
  }),
  on(loadPublicDataSetMetricsSuccess, (state: DataSetMetricsState, action: TypedAction<string> & { metrics: Serializers<DataSetMetric> }): DataSetMetricsState => {
    const serializedSource: Serializers<DataSetMetric> = action.metrics.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'source');
    const serializedNormalized: Serializers<DataSetMetric> = action.metrics.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'normalized');
    const serializedCalculated: Serializers<DataSetMetric> = action.metrics.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'calculated');

    const componentObjectsAll: MetricComponentObjects = action.metrics.map((metric: Serializer<DataSetMetric>): MetricComponentObject => new MetricComponentObject(metric));
    const componentObjectsSource: MetricComponentObjects = componentObjectsAll.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'source');
    const componentObjectsNormalized: MetricComponentObjects = componentObjectsAll.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'normalized');
    const componentObjectsCalculated: MetricComponentObjects = componentObjectsAll.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'calculated');

    return {
      ...state,
      serialized: {
        all: action.metrics,
        source: serializedSource,
        normalized: serializedNormalized,
        calculated: serializedCalculated
      },
      componentObjects: {
        all: componentObjectsAll,
        source: componentObjectsSource,
        normalized: componentObjectsNormalized,
        calculated: componentObjectsCalculated
      },
      loading: false,
      loaded: true
    };
  }),
  on(updateDataSetMetrics, (state: DataSetMetricsState): DataSetMetricsState => {
    return {
      ...state,
      loading: true,
      loaded: false
    };
  }),
  on(updateDataSetMetricsSuccess, (state: DataSetMetricsState, action: TypedAction<string> & { metrics: Serializers<DataSetMetric> }): DataSetMetricsState => {
    const serializedSource: Serializers<DataSetMetric> = action.metrics.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'source');
    const serializedNormalized: Serializers<DataSetMetric> = action.metrics.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'normalized');
    const serializedCalculated: Serializers<DataSetMetric> = action.metrics.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'calculated');

    action.metrics.forEach((metric: Serializer<DataSetMetric>): void => {
      const componentObject: MetricComponentObject = state.componentObjects.all.find((componentObject: MetricComponentObject): boolean => componentObject.payload.id === metric.id);

      if (componentObject) {
        componentObject.update(metric);
      } else {
        state.componentObjects.all.push(new MetricComponentObject(metric));
      }
    });

    state.componentObjects.all = state.componentObjects.all.filter((metric: MetricComponentObject): boolean => action.metrics.includes(metric.payload as Serializer<DataSetMetric>));

    return {
      ...state,
      serialized: {
        all: action.metrics,
        source: serializedSource,
        normalized: serializedNormalized,
        calculated: serializedCalculated
      },
      componentObjects: {
        all: _sort(state.componentObjects.all),
        source: _sort(state.componentObjects.all.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'source')),
        normalized: _sort(state.componentObjects.all.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'normalized')),
        calculated: _sort(state.componentObjects.all.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'calculated'))
      },
      loading: false,
      loaded: true
    };
  }),
  on(createDataSetMetricSuccess, (state: DataSetMetricsState, action: TypedAction<string> & { metric: Serializer<DataSetMetric> }): DataSetMetricsState => {
    const serializedAll: Serializers<DataSetMetric> = [ ...state.serialized.all, action.metric ];
    const serializedSource: Serializers<DataSetMetric> = serializedAll.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'source');
    const serializedNormalized: Serializers<DataSetMetric> = serializedAll.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'normalized');
    const serializedCalculated: Serializers<DataSetMetric> = serializedAll.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'calculated');

    const componentObjectsAll: MetricComponentObjects = [ ...state.componentObjects.all, new MetricComponentObject(action.metric) ];
    const componentObjectsSource: MetricComponentObjects = componentObjectsAll.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'source');
    const componentObjectsNormalized: MetricComponentObjects = componentObjectsAll.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'normalized');
    const componentObjectsCalculated: MetricComponentObjects = componentObjectsAll.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'calculated');

    return {
      ...state,
      serialized: {
        all: serializedAll,
        source: serializedSource,
        normalized: serializedNormalized,
        calculated: serializedCalculated
      },
      componentObjects: {
        all: _sort(componentObjectsAll),
        source: _sort(componentObjectsSource),
        normalized: _sort(componentObjectsNormalized),
        calculated: _sort(componentObjectsCalculated)
      }
    };
  }),
  on(updateDataSetMetricSuccess, (state: DataSetMetricsState, action: TypedAction<string> & { metric: Serializer<DataSetMetric> }): DataSetMetricsState => {
    const serializedAll: Serializers<DataSetMetric> = state.serialized.all.map((metric: Serializer<DataSetMetric>): Serializer<DataSetMetric> => metric.id === action.metric.id && action.metric || metric);
    const serializedSource: Serializers<DataSetMetric> = serializedAll.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'source');
    const serializedNormalized: Serializers<DataSetMetric> = serializedAll.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'normalized');
    const serializedCalculated: Serializers<DataSetMetric> = serializedAll.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'calculated');

    state.componentObjects.all.find((metric: MetricComponentObject): boolean => metric.payload.id === action.metric.id).update(action.metric);

    return {
      ...state,
      serialized: {
        all: serializedAll,
        source: serializedSource,
        normalized: serializedNormalized,
        calculated: serializedCalculated
      },
      componentObjects: {
        all: _sort(state.componentObjects.all),
        source: _sort(state.componentObjects.source),
        normalized: _sort(state.componentObjects.normalized),
        calculated: _sort(state.componentObjects.calculated),
      }
    };
  }),
  on(deleteDataSetMetricSuccess, (state: DataSetMetricsState, action: TypedAction<string> & { metric: Serializer<DataSetMetric> }): DataSetMetricsState => {
    const serializedAll: Serializers<DataSetMetric> = state.serialized.all.filter((metric: Serializer<DataSetMetric>): boolean => metric.id !== action.metric.id);
    const serializedSource: Serializers<DataSetMetric> = serializedAll.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'source');
    const serializedNormalized: Serializers<DataSetMetric> = serializedAll.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'normalized');
    const serializedCalculated: Serializers<DataSetMetric> = serializedAll.filter((metric: Serializer<DataSetMetric>): boolean => metric.attributes.type === 'calculated');

    const componentObjectsAll: MetricComponentObjects = state.componentObjects.all.filter((metric: MetricComponentObject): boolean => metric.payload.id !== action.metric.id);
    const componentObjectsSource: MetricComponentObjects = componentObjectsAll.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'source');
    const componentObjectsNormalized: MetricComponentObjects = componentObjectsAll.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'normalized');
    const componentObjectsCalculated: MetricComponentObjects = componentObjectsAll.filter((metric: MetricComponentObject): boolean => (metric.payload.attributes as DataSetMetric).type === 'calculated');

    return {
      ...state,
      serialized: {
        all: serializedAll,
        source: serializedSource,
        normalized: serializedNormalized,
        calculated: serializedCalculated
      },
      componentObjects: {
        all: componentObjectsAll,
        source: componentObjectsSource,
        normalized: componentObjectsNormalized,
        calculated: componentObjectsCalculated
      }
    };
  }),
);

export const dataSetMetricsReducer: ActionReducer<DataSetMetricsState> = (state: DataSetMetricsState, action: Action) => {
  return _reducer(state, action);
}

function _sort(metrics: MetricComponentObjects): MetricComponentObjects {
  return metrics.sort((a: MetricComponentObject, b: MetricComponentObject): number => Sort.alphaAsc(a.payload.attributes.name, b.payload.attributes.name));
}
